import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaPlay
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { getAllStories } from '../data/exampleStories';
import BookReader from '../components/BookReader';
import { applyImageFallback, getStoryCoverFallback } from '../config/imageFallbacks';
import '../styles/examples.css';

const MIN_SLIDER_ITEMS = 12;
const asArray = (value) => (Array.isArray(value) ? value : []);
const getLoopedSliderItems = (items) => {
  if (items.length === 0) {
    return [];
  }

  const itemCount = Math.ceil(MIN_SLIDER_ITEMS / items.length) * items.length;

  return Array.from(
    { length: itemCount },
    (_, index) => items[index % items.length]
  );
};

const ExamplesPage = () => {
  const [selectedStory, setSelectedStory] = useState(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const { t } = useTranslation();

  const stories = getAllStories();

  const localizeStory = (story) => ({
    ...story,
    title: t(`stories.${story.id}.title`, { defaultValue: story.title }),
    subtitle: t(`stories.${story.id}.subtitle`, { defaultValue: story.subtitle }),
    need: t(`stories.${story.id}.need`, { defaultValue: story.need }),
    description: t(`stories.${story.id}.description`, { defaultValue: story.description })
  });

  const availableStories = stories.filter((story) => !story.comingSoon);
  const localizedStories = availableStories.slice(0, 3).map(localizeStory);
  const coverBooks = asArray(t('examplesPage.coverBooks', { returnObjects: true })).filter((book) => book.image);
  const sliderBaseBooks = coverBooks.length > 0 ? coverBooks : localizedStories;
  const sliderBooks = getLoopedSliderItems(sliderBaseBooks);

  const storyFlows = t('examplesPage.storyFlows', { returnObjects: true });
  const getStoryFlowCopy = (story, index) => {
    if (storyFlows && typeof storyFlows === 'object') {
      return storyFlows[story.id] || storyFlows[index + 1] || story.description;
    }

    return story.description;
  };

  const handleReadStory = (story) => {
    setSelectedStory(story);
    setShowPDFViewer(true);
  };

  const handleClosePDFViewer = () => {
    setShowPDFViewer(false);
    setSelectedStory(null);
  };

  return (
    <div className="examples-page examples-v2">
      <section className="examples-v2-section examples-v2-cover-slider-section">
        <div className="container">
          <div className="examples-v2-section-header">
            <h2>{t('examplesPage.featuresTitle')}</h2>
            <p>{t('examplesPage.featuresSubtitle')}</p>
          </div>
        </div>

        {sliderBooks.length > 0 && (
          <div className="examples-v2-cover-marquee" aria-label={t('examplesPage.featuresTitle')}>
            {[false, true].map((isDuplicate) => (
              <div
                key={isDuplicate ? 'duplicate-track' : 'primary-track'}
                className="examples-v2-cover-track"
                aria-hidden={isDuplicate}
              >
                {sliderBooks.map((book, index) => (
                  <Link
                    key={`${isDuplicate ? 'duplicate' : 'primary'}-${book.id || book.title}-${index}`}
                    to={book.href || '/create-story'}
                    className="examples-v2-cover-tile"
                    tabIndex={isDuplicate ? -1 : undefined}
                  >
                    <span className="examples-v2-cover-flip">
                      <span className="examples-v2-cover-face examples-v2-cover-front">
                        <img
                          src={book.image || book.coverImage}
                          alt={book.alt || book.title}
                          onError={(e) => applyImageFallback(e, getStoryCoverFallback(book.id || index + 1))}
                        />
                      </span>
                      <span className="examples-v2-cover-face examples-v2-cover-back">
                        <strong className="examples-v2-cover-title">{book.title}</strong>
                        <span className="examples-v2-cover-need">
                          <em>{t('examplesPage.needBadgeLabel')}</em>
                          <b>{book.need}</b>
                        </span>
                        <small className="examples-v2-cover-age">
                          {t('examplesPage.ageLabel', { age: book.age })}
                        </small>
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="examples-v2-section examples-v2-gallery-section">
        <div className="container">
          <div className="examples-v2-section-header">
            <h2>{t('examplesPage.storiesTitle')}</h2>
            <p>{t('examplesPage.storiesSubtitle')}</p>
          </div>

          <div className="examples-v2-flow-list">
            {localizedStories.map((story, index) => (
              <article
                key={story.id}
                className="examples-v2-flow-card"
              >
                <div className="examples-v2-flow-need">
                  <span className="examples-v2-flow-step">{`0${index + 1}`}</span>
                  <blockquote>{story.need}</blockquote>
                </div>

                <div className="examples-v2-flow-connector" aria-hidden="true">
                  <span>→</span>
                  <small>{t('examplesPage.flowConnectorLabel')}</small>
                </div>

                <div className="examples-v2-flow-book">
                  <div className="examples-v2-flow-cover">
                    <img
                      src={story.coverImage}
                      alt={story.title}
                      onError={(e) => applyImageFallback(e, getStoryCoverFallback(story.id))}
                    />
                  </div>

                  <div className="examples-v2-flow-copy">
                    <div className="examples-v2-flow-meta">
                      <span>{t('examplesPage.ageLabel', { age: story.age })}</span>
                      <span>{story.subtitle}</span>
                    </div>
                    <h3>{story.title}</h3>
                    <p>{getStoryFlowCopy(story, index)}</p>

                    <div className="examples-v2-flow-actions">
                      <button
                        className="examples-v2-preview-button"
                        onClick={() => handleReadStory(story)}
                        type="button"
                      >
                        <FaPlay aria-hidden="true" />
                        {t('examplesPage.readStory')}
                      </button>
                      <Link to={`/story/${story.id}`} className="examples-v2-detail-link">
                        {t('examplesPage.storyDetails')}
                        <FaArrowRight aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="examples-v2-final-cta">
        <div className="container">
          <div className="examples-v2-final-cta-inner">
            <h2>{t('examplesPage.ctaTitle')}</h2>
            <p>{t('examplesPage.ctaSubtitle')}</p>
            <Link to="/create-story" className="examples-v2-primary-cta">
              {t('examplesPage.ctaPrimary')}
              <FaArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {showPDFViewer && selectedStory && (
        <BookReader
          pdfUrl={selectedStory.pdfUrl || '/pdfs/alex_compressed.pdf'}
          onClose={handleClosePDFViewer}
        />
      )}
    </div>
  );
};

export default ExamplesPage;
